<template>
    <div class="mobile-form-container">
        <branding-header />

        <h3 class="form-title mt-2">Need to Update Your Order?</h3>
        <p class="text-left mb-4">
            If something needs to be changed with your order, we're here to help!
            Enter your information and we will text you at the number that you provide. (Available Monday through
            Friday, 9am - 5pm & Saturday-Sunday, 10am-5pm)
        </p>

        <v-form
            ref="changeForm"
            v-model="form_valid"
            @submit.prevent="submitRequest"
        >
            <div class="info-section">
                <h4 class="section-title">
                    <v-icon
                        left
                        color="primary"
                        size="20"
                    >mdi-account</v-icon>
                    Contact Information
                </h4>

                <v-text-field
                    v-model="change_request.name"
                    label="Your Name"
                    outlined
                    dense
                    :rules="[rules.required]"
                    hide-details="auto"
                    class="mobile-input"
                />

                <v-text-field
                    v-model="change_request.email"
                    label="Email Address"
                    type="email"
                    outlined
                    dense
                    :rules="[rules.required, rules.email]"
                    hide-details="auto"
                    class="mobile-input"
                />

                <v-text-field
                    v-model="change_request.phone"
                    label="Phone Number (where we can text you)"
                    type="tel"
                    outlined
                    dense
                    :rules="[rules.required, rules.phone]"
                    hide-details="auto"
                    class="mobile-input"
                />

                <v-text-field
                    v-model="change_request.order_reference"
                    label="Order Number or Reference"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    class="mobile-input"
                />

                <v-textarea
                    v-model="change_request.description"
                    label="What needs changed?"
                    outlined
                    rows="4"
                    :rules="[rules.required]"
                    hide-details="auto"
                    class="mobile-input"
                    placeholder="Describe the changes you need to your order..."
                />

                <div class="consent-text">
                    <em>
                        By submitting this form, you give Barrel Creek Vineyards permission to send text
                        messages with info, promotions, and offers to the number you provided, including use of
                        an automated system, even if that number is listed on any do not call list. Consent is
                        not a condition of purchase. Texts are recorded. Message and/or data rates may apply.
                        Use is subject to <a
                            href="#"
                            @click.prevent="showTerms"
                        >Terms</a> with an arbitration clause
                        and a <a
                            href="#"
                            @click.prevent="showPrivacy"
                        >Privacy Policy</a>.
                    </em>
                </div>
            </div>

            <div class="mobile-actions">
                <v-btn
                    color="primary"
                    :disabled="!form_valid || submitting"
                    :loading="submitting"
                    block
                    @click="submitRequest"
                    class="action-btn submit-btn"
                >
                    <v-icon left>mdi-send</v-icon>
                    {{ submitting ? 'Sending Request...' : 'Agree and Send' }}
                </v-btn>
            </div>
        </v-form>

        <placeholder-dialog ref="placeholderDialog" />
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';
import Validate from '@/js/validate.js';

export default {
    name: 'ChangeRequestForm',

    components: {
        BrandingHeader,
        PlaceholderDialog
    },

    props: {
        order_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            form_valid: false,
            submitting: false,
            change_request: {
                name: this.order_data.customer_name,
                email: this.order_data.email,
                phone: this.order_data.phone,
                order_reference: this.order_data.order_number,
                description: `I will not be home on the scheduled delivery date. Can we reschedule?`
            },
            rules: {
                required: value => !!value || 'This field is required',
                email: value => {
                    if (!Validate.isEmailAddress(value?.trim())) {
                        return 'Please enter a valid email address';
                    }
                    return true;
                },
                phone: value => {
                    if (!Validate.isUSPhoneNumber(value?.trim())) {
                        return 'A valid phone number is required';
                    }
                    return true;
                },
            },
        }
    },

    methods: {
        async submitRequest() {
            if (!this.form_valid) return;

            this.submitting = true;
            await new Promise(resolve => setTimeout(resolve, 1000));
            this.submitting = false;

            this.$emit('submitted', {
                type: 'change_request',
                data: this.change_request
            });
        },

        showTerms() {
            this.$refs.placeholderDialog.show('Terms & Conditions');
        },

        showPrivacy() {
            this.$refs.placeholderDialog.show('Privacy Policy');
        }
    },
}
</script>

<style lang="scss" scoped>
.info-section {
    background: #f8f9fa;
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border: 1px solid #e9ecef;

    .section-title {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: var(--v-primary-base);
        gap: 0.25rem;
    }
}

.consent-text {
    margin-top: 0.75rem;
    text-align: left;

    em {
        font-size: 0.7rem;
        line-height: 1.25;
        color: var(--color-text, #666);
        display: block;
    }

    a {
        color: var(--v-primary-base);
        text-decoration: underline;
    }
}
</style>
